import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import {
  SELLER_ME_QUERY_KEY,
  sellerOnboardingApi,
  type SellerApplication,
} from '@modules/seller-onboarding/api';
import { SELLER_APPLICATION_REJECTION_LABELS } from '@modules/seller-onboarding/seller-application-rejection-labels';
import { sellerNeedsRevisionRegistrationWizard } from '@modules/seller-onboarding/moderation-revision';
import { SELLER_ACCOUNT_BLOCK_REASON_LABELS } from '@modules/seller-onboarding/seller-account-block.labels';
import type { SellerAccountBlockCabinetInfo } from '@modules/seller-onboarding/seller-account-block.types';
import { SELLER_STAFF_SECTION_LABELS, isSellerOwner } from '@modules/seller-staff';
import { Button } from '@shared/ui';
import SellerDashboardSection from './SellerDashboardSection';
import mascotModeration from '../../assets/maskot2.png';
import mascotBlocked from '../../assets/maskot-blocked.png';
import mascotRejected from '../../assets/maskot-rejected.png';
import '@shared/styles/page.css';
import './SellerMainPage.css';

function formatSellerAccountBlockedUntil(block: SellerAccountBlockCabinetInfo): string {
  if (block.permanent) {
    return 'Срок блокировки не ограничен. Вопрос возможного восстановления доступа — только через поддержку маркетплейса после проверки ситуации.';
  }
  if (block.awaitingManualReview) {
    return 'Автоматическая разблокировка по дате не назначена — обратитесь в поддержку для выяснения обстоятельств.';
  }
  if (block.blockedUntil) {
    try {
      const d = new Date(block.blockedUntil);
      return `Ожидаемая автоматическая разблокировка: ${d.toLocaleString('ru-RU', {
        dateStyle: 'long',
        timeStyle: 'short',
      })}`;
    } catch {
      return 'Информация о сроке разблокировки уточняется.';
    }
  }
  return 'Срок автоматической разблокировки не задан.';
}

function ModerationRejectionMessage({ app }: { app: SellerApplication }) {
  const kind = app.moderationRejectionKind;
  const comment = app.moderationRejectionComment?.trim();
  const full = app.moderationRejectionReason?.trim() ?? '';

  if (kind && kind in SELLER_APPLICATION_REJECTION_LABELS) {
    const title =
      SELLER_APPLICATION_REJECTION_LABELS[kind as keyof typeof SELLER_APPLICATION_REJECTION_LABELS];
    return (
      <div className="seller-main-status-card__rejection-body">
        <p className="seller-main-status-card__reason">{title}</p>
        {comment ? (
          <p className="seller-main-status-card__text seller-main-status-card__comment">
            {comment}
          </p>
        ) : null}
      </div>
    );
  }

  const blocks = full.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (blocks.length >= 2) {
    return (
      <div className="seller-main-status-card__rejection-body">
        <p className="seller-main-status-card__reason">{blocks[0]}</p>
        <p className="seller-main-status-card__text seller-main-status-card__comment">
          {blocks.slice(1).join('\n\n')}
        </p>
      </div>
    );
  }

  return (
    <p className="seller-main-status-card__reason seller-main-status-card__reason--plain">
      {full || 'Причина не указана'}
    </p>
  );
}

/** Главная кабинета продавца: статус заявки (до одобрения) и дашборд продаж (после одобрения). */
export default function SellerMainPage() {
  const navigate = useNavigate();
  const { data: app, isLoading } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });


  if (isLoading || !app) {
    return (
      <Center className="seller-main-page" mih={320}>
        <Loader />
      </Center>
    );
  }

  const sellerBlockInfo = app.seller?.accountBlock;
  if (app.status === 'APPROVED' && sellerBlockInfo) {
    const reasonTitle =
      SELLER_ACCOUNT_BLOCK_REASON_LABELS[sellerBlockInfo.reason] ??
      sellerBlockInfo.reason;

    return (
      <div className="seller-main-page seller-main-page--status-panel">
        <div className="seller-main-status-row">
          <img
            src={mascotBlocked}
            alt=""
            className="seller-main-status-mascot"
            aria-hidden
          />
          <div className="seller-main-status-card seller-main-status-card--blocked">
            <p className="seller-main-status-card__eyebrow seller-main-status-card__eyebrow--blocked">
              Доступ ограничен
            </p>
            <h1 className="seller-main-status-card__title seller-main-status-card__title--blocked">
              Кабинет заблокирован
            </h1>
            <p className="seller-main-status-card__reason">{reasonTitle}</p>
            {sellerBlockInfo.comment?.trim() ? (
              <p className="seller-main-status-card__text seller-main-status-card__comment">
                {sellerBlockInfo.comment.trim()}
              </p>
            ) : null}
            <p className="seller-main-status-card__text">
              {formatSellerAccountBlockedUntil(sellerBlockInfo)}
            </p>
            <Button
              type="button"
              variant="primary"
              fullWidth
              className="seller-main-status-card__cta"
              onClick={() => navigate('/support-chat')}
            >
              Написать в поддержку
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (app.status === 'PENDING_MODERATION') {
    return (
      <div className="seller-main-page seller-main-page--status-panel">
        <div className="seller-main-status-row">
          <img
            src={mascotModeration}
            alt=""
            className="seller-main-status-mascot"
            aria-hidden
          />
          <div className="seller-main-status-card">
            <p className="seller-main-status-card__eyebrow">Заявка на проверке</p>
            <h1 className="seller-main-status-card__title seller-main-status-card__title--pending">
              Магазин на модерации
            </h1>
            <p className="seller-main-status-card__text">
              Заявка отправлена. Мы проверим документы — статус обновится здесь после решения
              модератора.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (app.status === 'APPROVED') {
    if (!isSellerOwner(app)) {
      const sectionLabels = (app.staffSections ?? [])
        .map((s) => SELLER_STAFF_SECTION_LABELS[s] ?? s)
        .join(', ');
      return (
        <div className="seller-main-page seller-main-page--status-panel">
          <Paper
            className="seller-main-status-card"
            shadow="md"
            radius="lg"
            p="xl"
            maw={440}
            w="100%"
          >
            <Stack gap="md">
              <Title order={3} size="h4" ta="center" fw={600}>
                Вы вошли как сотрудник
              </Title>
              <Text size="sm" ta="center" style={{ lineHeight: 1.55, color: '#495057' }}>
                {sectionLabels
                  ? `Доступны разделы: ${sectionLabels}.`
                  : 'Владелец магазина пока не выдал разделы.'}
              </Text>
            </Stack>
          </Paper>
        </div>
      );
    }
    return (
      <div className="seller-main-page page-container seller-main-page--scroll-inner">
        <div className="seller-main-scroll">
          <SellerDashboardSection
            hideApprovedOnboardingBanner={Boolean(
              app.user?.hideSellerApprovedOnboardingBanner,
            )}
          />
        </div>
      </div>
    );
  }

  if (app.status === 'REJECTED') {
    const canEditRegistration = sellerNeedsRevisionRegistrationWizard(
      app.status,
      app.moderationRejectionReason,
      app.moderationRejectionKind,
    );
    return (
      <div className="seller-main-page seller-main-page--status-panel">
        <div className="seller-main-status-row">
          <img
            src={mascotRejected}
            alt=""
            className="seller-main-status-mascot"
            aria-hidden
          />
          <div className="seller-main-status-card seller-main-status-card--rejected">
            <h1 className="seller-main-status-card__title seller-main-status-card__title--rejected">
              Заявка отклонена
            </h1>
            <div className="seller-main-status-card__rejection">
              <p className="seller-main-status-card__rejection-label">Причина отказа</p>
              <ModerationRejectionMessage app={app} />
            </div>
            {canEditRegistration ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  className="seller-main-status-card__cta"
                  onClick={() => navigate('/registration')}
                >
                  Изменить данные
                </Button>
                <p className="seller-main-status-card__hint">
                  Откройте анкету регистрации — можно заменить нужные данные и отправить заявку
                  снова.
                </p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-main-page page-container">
      <Text c="dimmed">Завершите регистрацию продавца.</Text>
    </div>
  );
}
